import { RiskChip } from "./RiskChip";
import { LogoMark } from "./Logo";
import { DISCLAIMER } from "@/lib/site";
import { riskScore, riskBand, RISK_BAND_LABEL } from "@/lib/templateContent";
import { splitEmergency, type RamsReport } from "@/lib/reports";
import { SCOPE_KEY, TITLE_KEY, ctlKey, seqKey, whoKey, ppeKey, subKey, compKey, emgKey, type DocEdits } from "@/lib/edits";
import { brandColor, safeLogo, headerTheme, DEFAULT_ACCENT, type Brand } from "@/lib/brand";

/** Inline click-to-edit text. Uncontrolled contentEditable, committed on blur;
 *  the key remounts it when an edit is applied/restored externally. */
function Editable({
  k,
  text,
  edited,
  onEdit,
  multiline = false,
  className = "",
}: {
  k: string;
  text: string;
  edited: boolean;
  onEdit?: (key: string, next: string | null) => void;
  multiline?: boolean;
  className?: string;
}) {
  if (!onEdit) return <span className={className}>{text}</span>;
  return (
    <>
      <span
        key={`${k}:${edited ? "e" : "o"}:${text}`}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onBlur={(e) => {
          const next = (e.currentTarget.textContent || "").trim();
          if (next !== text.trim()) onEdit(k, next);
        }}
        onKeyDown={
          multiline
            ? undefined
            : (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).blur();
                }
              }
        }
        className={`-mx-0.5 cursor-text rounded-sm px-0.5 outline-none transition-colors hover:bg-emerald-tint/70 focus:bg-emerald-tint/70 focus:ring-1 focus:ring-emerald/40 ${className}`}
      >
        {text}
      </span>
      {edited && (
        <button
          type="button"
          onClick={() => onEdit(k, null)}
          title="Edited — restore the original text"
          className="no-print ml-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-tint px-1.5 py-0.5 align-middle text-[0.58rem] font-semibold uppercase tracking-wide text-emerald-deep hover:bg-emerald/20"
        >
          edited ↺
        </button>
      )}
    </>
  );
}

/** Struck-through ghost of a removed item (preview only — the PDF drops it). */
function RemovedGhost({ k, text, onEdit }: { k: string; text: string; onEdit: (key: string, next: string | null) => void }) {
  return (
    <span className="no-print text-muted/60">
      <span className="line-through">{text}</span>
      <button
        type="button"
        onClick={() => onEdit(k, null)}
        className="ml-1.5 rounded-full bg-mist px-1.5 py-0.5 align-middle text-[0.58rem] font-semibold uppercase tracking-wide text-body hover:bg-line"
      >
        removed ↺ restore
      </button>
    </span>
  );
}

/**
 * The live RAMS document: renders the composed, human-reviewed report data with
 * the user's site details and hazard scope applied. Pure render — every keystroke
 * and toggle in the builder re-renders this instantly. Also the print surface
 * (#rams-document) for the PDF download.
 */
export function ReportDocument({
  report,
  values,
  enabled,
  permits,
  scopeLines = [],
  edits = {},
  onEdit,
  draft = false,
  issueDate,
  brand,
}: {
  report: RamsReport;
  values: Record<string, string>;
  enabled: Set<string>;
  /** Effective permits (base ± trigger-question effects). Defaults to the report's. */
  permits?: string[];
  /** Job-specific notes from the trigger questions, rendered under Scope of works. */
  scopeLines?: string[];
  /** In-document edits keyed per item ("" = removed). */
  edits?: DocEdits;
  /** Present = the document is editable; called with null to restore. */
  onEdit?: (key: string, next: string | null) => void;
  /** Below full readiness — print fallback gets an "incomplete draft" watermark. */
  draft?: boolean;
  /** ISO date stamped on the document (set client-side to avoid hydration mismatch). */
  issueDate?: string;
  /** Optional document branding — the user's logo and accent colour. */
  brand?: Brand;
}) {
  const hazards = report.hazards.filter((h) => enabled.has(h.hazardId));
  const effectivePermits = permits ?? report.permitsRequired;
  const val = (key: string) => (values[key] || "").trim();
  // Branding: drive the section underlines via the --accent custom property, swap
  // the RamsDocs mark, and recolour the header band with auto-contrasted text.
  const accent = brandColor(brand?.color);
  const brandLogo = safeLogo(brand?.logo);
  const headerColor = brandColor(brand?.headerColor);
  const ht = headerTheme(headerColor);
  const headerStyle: React.CSSProperties = {};
  if (headerColor) {
    headerStyle.background = ht.bg;
    headerStyle.color = ht.fg;
  }
  if (accent) headerStyle.borderTop = `4px solid ${accent}`;
  const legislation = groupCitations(report, hazards);
  const text = (key: string, original: string) => (edits[key] !== undefined ? edits[key] : original);
  const scopeText = text(SCOPE_KEY, report.scopeOfWorks).trim() || report.scopeOfWorks;
  const titleText = text(TITLE_KEY, report.title).trim() || report.title;

  return (
    <div
      id="rams-document"
      className="overflow-hidden rounded-2xl border border-line bg-white text-ink shadow-float"
      style={{ ["--accent" as string]: accent || DEFAULT_ACCENT } as React.CSSProperties}
    >
      {/* Brand header */}
      <div className={`px-7 py-6 ${headerColor ? "" : "hero-navy text-white"}`} style={headerStyle}>
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2 font-display text-sm font-bold tracking-tight">
            {brandLogo ? (
              <img src={brandLogo} alt="" className="h-7 w-auto max-w-[170px] object-contain" />
            ) : (
              <>
                <LogoMark className="h-6 w-6" /> RamsDocs
              </>
            )}
          </span>
          <span
            className="rounded-full border px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider"
            style={{ borderColor: headerColor ? ht.faint : "rgba(255,255,255,0.3)", color: headerColor ? ht.muted : "rgba(255,255,255,0.85)" }}
          >
            For competent-person review
          </span>
        </div>
        <h2 className="mt-4 font-display text-2xl font-bold leading-tight" style={{ color: headerColor ? ht.fg : "#ffffff" }}>
          <Editable k={TITLE_KEY} text={titleText} edited={edits[TITLE_KEY] !== undefined} onEdit={onEdit} className="hover:!bg-white/15 focus:!bg-white/15" />
        </h2>
        <p className="mt-1 text-[0.8rem]" style={{ color: headerColor ? ht.muted : "rgba(255,255,255,0.7)" }}>
          {report.trade} · {report.task} · Document ref {report.id.toUpperCase()}-V{report.version}
          {" · "}Rev {val("revision") || "A"}
          {issueDate && <> · Issued {formatReviewDate(issueDate)}</>}
          {report.lastReviewed && <> · Library reviewed {formatReviewDate(report.lastReviewed)}</>}
        </p>
      </div>

      {/* Print fallback (window.print) — the DocRaptor path watermarks in pdfHtml. */}
      {draft && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-50 hidden items-center justify-center print:flex">
          <span className="-rotate-[24deg] text-center text-5xl font-extrabold uppercase tracking-[0.18em] text-risk-veryhigh/15">
            Incomplete — not for submission
          </span>
        </div>
      )}

      <div className="space-y-7 px-7 py-6 text-[0.82rem] leading-relaxed text-body">
        {/* Project control details */}
        <Section title="Project control details">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3">
            {/* Optional fields appear only once filled; required ones always show. */}
            {report.siteFields.filter((f) => f.required || val(f.key)).map((f) => {
              const v = val(f.key);
              return (
                <div key={f.key} className="avoid-break">
                  <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted">
                    {f.label}
                    {f.required && <span className="text-risk-high"> *</span>}
                  </p>
                  {v ? (
                    <p className="mt-0.5 whitespace-pre-line font-medium text-ink">{v}</p>
                  ) : (
                    <p className="mt-0.5 italic text-muted">To be completed</p>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Scope of works */}
        <Section title="Scope of works">
          <p>
            <Editable k={SCOPE_KEY} text={scopeText} edited={edits[SCOPE_KEY] !== undefined} onEdit={onEdit} multiline />
          </p>
          {val("workLocationOnSite") && (
            <p className="mt-2">
              <span className="font-semibold text-ink">Work location:</span> {val("workLocationOnSite")}
              {val("siteAddress") && <>, {val("siteAddress").replace(/\n+/g, ", ")}</>}
            </p>
          )}
          {scopeLines.length > 0 && (
            <div className="avoid-break mt-3 rounded-lg bg-mist px-3.5 py-2.5">
              <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted">Job-specific arrangements</p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5">
                {scopeLines.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
          )}
        </Section>

        {/* Risk rating matrix & tolerability — the scoring key, ahead of the
            scored hazards so the reader meets the methodology before the
            results (the ALARP statement below cites the tolerability table). */}
        <Section title="Risk rating matrix & tolerability">
          <RiskMatrix />
          <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {TOLERABILITY.map((t) => (
              <div key={t.band} className="avoid-break rounded-lg border border-line p-2.5">
                <p className="text-[0.72rem] font-bold text-ink">{t.band}</p>
                <p className="mt-0.5 text-[0.7rem] leading-snug text-muted">{t.action}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Risk assessment summary */}
        <Section title="Risk assessment">
          <div className="overflow-hidden rounded-lg border border-line">
            <div className="grid grid-cols-[1fr_5.2rem_5.2rem] gap-x-3 border-b border-line bg-mist px-3 py-2 text-[0.62rem] font-semibold uppercase tracking-wider text-muted">
              <span>Hazard</span>
              <span className="text-center">Initial</span>
              <span className="text-center">Residual</span>
            </div>
            {hazards.map((h) => (
              <div key={h.hazardId} className="grid grid-cols-[1fr_5.2rem_5.2rem] items-center gap-x-3 border-b border-line px-3 py-2 last:border-0">
                <span className="leading-snug text-ink">{h.hazard}</span>
                <span className="flex justify-center"><RiskChip rating={h.initialRisk} /></span>
                <span className="flex justify-center"><RiskChip rating={h.residualRisk} /></span>
              </div>
            ))}
            {hazards.length === 0 && (
              <p className="px-3 py-3 italic text-muted">No hazards in scope — switch at least one hazard on in the builder.</p>
            )}
          </div>
          <p className="mt-2 text-[0.68rem] text-muted">
            Risk score = likelihood (1–5) × severity (1–5). Bands: 1–4 low · 5–9 medium · 10–15 high · 16–25 very high.
            Residual assumes the stated controls are in place.
          </p>
          {alarpStatement(hazards) && (
            <p className="avoid-break mt-2 rounded-lg bg-mist px-3.5 py-2.5 text-[0.74rem]">
              <span className="font-semibold text-ink">ALARP justification: </span>
              {alarpStatement(hazards)}
            </p>
          )}
        </Section>

        {/* Hazard detail */}
        <Section title="Hazards & control measures">
          <div className="space-y-4">
            {hazards.map((h) => {
              const initial = riskScore(h.initialRisk);
              const residual = riskScore(h.residualRisk);
              return (
                <div key={h.hazardId} className="avoid-break rounded-lg border border-line p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="font-display text-[0.92rem] font-bold text-ink">{h.hazard}</h4>
                    <span className="flex items-center gap-2 text-[0.66rem] font-medium text-muted">
                      <RiskChip rating={h.initialRisk} /> {RISK_BAND_LABEL[riskBand(initial)]}
                      <span aria-hidden>→</span>
                      <RiskChip rating={h.residualRisk} /> {RISK_BAND_LABEL[riskBand(residual)]}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[0.74rem] text-muted">
                    <span className="font-semibold text-body">Who is at risk:</span>{" "}
                    <Editable k={whoKey(h.hazardId)} text={text(whoKey(h.hazardId), h.whoIsAtRisk.join(", ")).trim() || h.whoIsAtRisk.join(", ")} edited={edits[whoKey(h.hazardId)] !== undefined} onEdit={onEdit} />
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {h.controls.map((c, ci) => {
                      const k = ctlKey(h.hazardId, ci);
                      const removed = onEdit && edits[k] === "";
                      return (
                        <li key={k} className={removed ? "no-print" : undefined}>
                          {removed ? (
                            <RemovedGhost k={k} text={c} onEdit={onEdit!} />
                          ) : (
                            <Editable k={k} text={text(k, c)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} multiline />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {h.citation && <p className="mt-2 text-[0.7rem] italic text-muted">Basis: {h.citation}</p>}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Method statement */}
        <Section title="Method statement — sequence of works">
          <ol className="space-y-2.5">
            {effectivePermits.length > 0 && (
              <li className="avoid-break flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[0.68rem] font-bold text-amber-800">!</span>
                <span>
                  <span className="font-semibold text-ink">Hold point — </span>
                  before any permit-controlled operation starts: obtain the required permit(s) ({effectivePermits.join("; ")})
                  from the issuer named in this document, display the live permit at the workface and brief its conditions to
                  the operatives. Work covered by a permit does not begin or resume without a live permit.
                </span>
              </li>
            )}
            {(() => {
              let n = 0;
              return report.sequenceOfWorks.map((s) => {
                const k = seqKey(s.order);
                const removed = onEdit && edits[k] === "";
                if (!removed) n += 1;
                return (
                  <li key={k} className={`avoid-break flex gap-3 ${removed ? "no-print" : ""}`}>
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.68rem] font-bold ${removed ? "bg-mist text-muted/60" : "bg-emerald-tint text-emerald-deep"}`}>
                      {removed ? "–" : n}
                    </span>
                    <span>
                      {removed ? (
                        <RemovedGhost k={k} text={s.description} onEdit={onEdit!} />
                      ) : (
                        <Editable k={k} text={text(k, s.description)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} multiline />
                      )}
                    </span>
                  </li>
                );
              });
            })()}
          </ol>
        </Section>

        {/* PPE */}
        <Section title="Personal protective equipment">
          <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {report.ppe.map((p, pi) => {
              const k = ppeKey(pi);
              const removed = onEdit && edits[k] === "";
              return (
                <li key={k} className={`flex gap-2 ${removed ? "no-print" : ""}`}>
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${removed ? "bg-line-2" : "bg-emerald"}`} />
                  {removed ? (
                    <RemovedGhost k={k} text={p} onEdit={onEdit!} />
                  ) : (
                    <Editable k={k} text={text(k, p)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} />
                  )}
                </li>
              );
            })}
          </ul>
        </Section>

        {/* Permits / substances / competence — always rendered: an explicit
            "none" declaration reads as assessed, silence reads as forgotten. */}
        <Section title="Permits, substances & competence">
            {effectivePermits.length > 0 ? (
              <p>
                <span className="font-semibold text-ink">Permits required:</span> {effectivePermits.join("; ")}
                {val("permitsRequired") && <> — {val("permitsRequired")}</>}
              </p>
            ) : val("permitsRequired") ? (
              <p>
                <span className="font-semibold text-ink">Permits:</span> {val("permitsRequired")}
              </p>
            ) : (
              <p>
                <span className="font-semibold text-ink">Permits required:</span> None identified for this task by this
                assessment, and none are triggered by the planned method. If the principal contractor&apos;s site rules
                require a permit for any activity here, that permit is obtained before the activity starts.
              </p>
            )}
            {report.substances.length === 0 && (
              <p className="mt-1.5">
                <span className="font-semibold text-ink">Substances (COSHH):</span> No hazardous substances are used or
                generated by this task as planned. If any substance is introduced (adhesives, sealants, fuels,
                significant dust), work on the affected activity stops until a COSHH assessment for that substance is
                completed and briefed.
              </p>
            )}
            {report.substances.length > 0 && (
              <p className="mt-1.5">
                <span className="font-semibold text-ink">Substances:</span>{" "}
                {report.substances.map((s, si) => {
                  const k = subKey(si);
                  const removed = onEdit && edits[k] === "";
                  return (
                    <span key={k} className={removed ? "no-print" : undefined}>
                      {si > 0 && <span className={removed ? "no-print text-muted/60" : undefined}>; </span>}
                      {removed ? (
                        <RemovedGhost k={k} text={s} onEdit={onEdit!} />
                      ) : (
                        <Editable k={k} text={text(k, s)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} />
                      )}
                    </span>
                  );
                })}
              </p>
            )}
            {report.substances.length > 0 && (
              <p className="mt-1.5 text-[0.74rem] text-muted">
                {val("substanceProducts") ? (
                  <>
                    <span className="font-semibold text-ink">Products in use:</span> {val("substanceProducts")}. The SDS
                    for each named product is held with this RAMS in the site file and was used to set the controls above.
                  </>
                ) : (
                  <>
                    Safety data sheets for the named products are held with this RAMS in the site file and were used to
                    set the controls above; product names and SDS dates are recorded in the briefing record.
                  </>
                )}
              </p>
            )}
            {report.competence.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {report.competence.map((c, ci) => {
                  const k = compKey(ci);
                  const removed = onEdit && edits[k] === "";
                  return (
                    <li key={k} className={removed ? "no-print" : undefined}>
                      {removed ? (
                        <RemovedGhost k={k} text={c.requirement} onEdit={onEdit!} />
                      ) : (
                        <Editable k={k} text={text(k, c.requirement)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} multiline />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {val("operatives") && (
              <p className="mt-2">
                <span className="font-semibold text-ink">Operative competence record:</span> {val("operatives")}
              </p>
            )}
          </Section>

        {/* Emergency & rescue */}
        <Section title="Emergency & rescue arrangements">
          {/* Site-specific emergency information — generic emergency text without a
              named first aider and assembly point is a standard rejection trigger. */}
          <div className="avoid-break mb-3 grid grid-cols-2 gap-x-6 gap-y-2.5 rounded-lg border border-line bg-mist/50 px-3.5 py-3 sm:grid-cols-3">
            {[
              { label: "Emergency services", value: "999 — Fire / Police / Ambulance", required: true },
              { label: "Address to give 999", value: val("siteAddress").replace(/\n+/g, ", "), required: true },
              { label: "First aider", value: val("firstAider"), required: true },
              { label: "Assembly point", value: val("assemblyPoint"), required: true },
              { label: "Nearest A&E", value: val("nearestAE"), required: false },
            ]
              .filter((i) => i.required || i.value)
              .map((i) => (
                <div key={i.label}>
                  <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted">{i.label}</p>
                  {i.value ? (
                    <p className="mt-0.5 font-medium text-ink">{i.value}</p>
                  ) : (
                    <p className="mt-0.5 italic text-muted">To be completed</p>
                  )}
                </div>
              ))}
          </div>
          <div className="space-y-2.5">
            {splitEmergency(report.emergencyRescue).map((s, i) => {
              const k = emgKey(i);
              const removed = onEdit && edits[k] === "";
              return (
                <p key={k} className={`avoid-break ${removed ? "no-print" : ""}`}>
                  {s.heading && <span className={`font-semibold ${removed ? "text-muted/60 line-through" : "text-ink"}`}>{s.heading}: </span>}
                  {removed ? (
                    <RemovedGhost k={k} text={s.body} onEdit={onEdit!} />
                  ) : (
                    <Editable k={k} text={text(k, s.body)} edited={edits[k] !== undefined && edits[k] !== ""} onEdit={onEdit} multiline />
                  )}
                </p>
              );
            })}
          </div>
        </Section>

        {/* Legislation */}
        <Section title="Legislation & guidance">
          <ul className="list-disc space-y-1 pl-5">
            {legislation.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </Section>

        {/* Briefing & sign-off */}
        <Section title="Briefing & sign-off">
          <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-3">
            <SignBox label="Prepared by" value={val("preparedBy")} />
            <SignBox label="Site supervisor / competent person" value={val("competentPerson")} />
            <SignBox label="Review date" value={val("reviewDate")} />
          </div>
          <p className="mt-4 text-[0.74rem] text-muted">
            All operatives must be briefed on this RAMS before work starts and sign below to confirm they understand it.
            Anyone joining the task later is briefed and signs before starting work; the supervisor re-briefs the team if
            the method, conditions or this document change.
          </p>
          <div className="mt-2 overflow-hidden rounded-lg border border-line">
            <div className="grid grid-cols-[1.2fr_1fr_0.6fr] border-b border-line bg-mist px-3 py-2 text-[0.62rem] font-semibold uppercase tracking-wider text-muted">
              <span>Name</span><span>Signature</span><span>Date</span>
            </div>
            {/* Pre-populated rows from the named operatives, then blanks for joiners */}
            {val("operatives")
              .split(";")
              .map((o) => o.split("—")[0].split("(")[0].trim())
              .filter(Boolean)
              .map((name) => (
                <div key={name} className="grid h-9 grid-cols-[1.2fr_1fr_0.6fr] border-b border-line px-3">
                  <span className="self-center text-[0.78rem] text-ink">{name}</span><span /><span />
                </div>
              ))}
            {Array.from({ length: val("operatives") ? 2 : 5 }, (_, i) => (
              <div key={i} className="grid h-9 grid-cols-[1.2fr_1fr_0.6fr] border-b border-line px-3 last:border-0">
                <span className="self-end border-line" /><span /><span />
              </div>
            ))}
          </div>
        </Section>

        <p className="border-t border-line pt-4 text-[0.7rem] leading-relaxed text-muted">{DISCLAIMER}</p>
      </div>
    </div>
  );
}

export const TOLERABILITY = [
  { band: "1–4 Low", action: "Proceed where the stated controls are implemented and briefed." },
  { band: "5–9 Medium", action: "Proceed after supervisor verification of the controls." },
  { band: "10–15 High", action: "Hold work until additional controls are agreed and verified by the competent person." },
  { band: "16–25 Very high", action: "Do not start. Re-plan the task to eliminate or substitute the hazard." },
];

export const MATRIX_SEVERITIES = ["Negligible", "Minor", "Moderate", "Major", "Catastrophic"];
export const MATRIX_LIKELIHOODS: [string, number][] = [
  ["Almost certain", 5],
  ["Likely", 4],
  ["Possible", 3],
  ["Unlikely", 2],
  ["Rare", 1],
];

/** Soft traffic-light tints per band (matrix cells; matches the engine report). */
export const BAND_TINT: Record<string, { bg: string; text: string }> = {
  low: { bg: "#e2f5ee", text: "#0e7a5c" },
  medium: { bg: "#fdf6e3", text: "#8a6200" },
  high: { bg: "#fdeee4", text: "#b34c13" },
  "very-high": { bg: "#fbeaea", text: "#b3261e" },
};

function RiskMatrix() {
  return (
    <div className="avoid-break overflow-hidden rounded-lg border border-line">
      <table className="w-full border-collapse text-center text-[0.66rem]">
        <thead>
          <tr>
            <th className="border-b border-r border-line bg-mist p-1.5 text-left text-[0.6rem] font-semibold uppercase tracking-wider text-muted">
              Likelihood ↓ · Severity →
            </th>
            {MATRIX_SEVERITIES.map((s) => (
              <th key={s} className="border-b border-line bg-mist p-1.5 text-[0.62rem] font-bold text-ink">{s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MATRIX_LIKELIHOODS.map(([label, l]) => (
            <tr key={label}>
              <th className="border-r border-t border-line p-1.5 text-right text-[0.62rem] font-bold text-ink">{label}</th>
              {MATRIX_SEVERITIES.map((_, i) => {
                const score = l * (i + 1);
                const band = riskBand(score);
                const tint = BAND_TINT[band];
                return (
                  <td key={i} className="border-t border-line p-1.5 font-bold uppercase tracking-wide" style={{ background: tint.bg, color: tint.text }}>
                    {RISK_BAND_LABEL[band]}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Auto-derived ALARP justification: where high-severity hazards keep their
 *  severity after controls (the consequence of failure is unchanged), explain
 *  that the controls drive likelihood down instead — the question a PC reviewer
 *  otherwise sends the document back to ask. Null when no hazard warrants it. */
export function alarpStatement(hazards: RamsReport["hazards"]): string | null {
  const highSev = hazards.filter((h) => h.residualRisk.severity >= 4);
  if (highSev.length === 0) return null;
  const names = highSev.map((h) => h.hazard).join("; ");
  return (
    `Controls follow the hierarchy of control (eliminate, substitute, engineering controls, safe systems of work, then PPE). ` +
    `For ${names} — the severity of the worst credible outcome cannot be reduced by on-site controls, so the stated controls ` +
    `reduce the likelihood of that outcome instead; residual risk is as low as is reasonably practicable (ALARP). ` +
    `Per the tolerability table, work on any hazard whose residual risk remains High or Very high must not start until ` +
    `additional controls are agreed and verified by the competent person.`
  );
}

/** "2026-06-04" → "4 Jun 2026" (en-GB, no time component). */
export function formatReviewDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${y}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2.5 border-b-2 pb-1.5 font-display text-[0.95rem] font-bold text-ink" style={{ borderColor: "var(--accent, #1ba37b)" }}>{title}</h3>
      {children}
    </section>
  );
}

function SignBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="avoid-break">
      <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted">{label}</p>
      {/* Populated from Site details — no write-in line for the name. */}
      {value ? <p className="mt-0.5 font-medium text-ink">{value}</p> : <p className="mt-0.5 italic text-muted">To be completed</p>}
      <p className="mt-2 text-[0.62rem] text-muted">Signature: ______________________</p>
    </div>
  );
}

/** Sources for the in-scope hazards plus the document-level citations, deduped.
 *  Per-hazard citations ("Controls for: X") are skipped when X is out of scope,
 *  so the legislation list follows the job answers. */
export function groupCitations(report: RamsReport, hazards: RamsReport["hazards"]): string[] {
  const inScope = new Set(hazards.map((h) => h.hazard));
  const sources = new Set<string>();
  sources.add("Health and Safety at Work etc. Act 1974");
  sources.add("Construction (Design and Management) Regulations 2015");
  for (const h of hazards) if (h.citation) sources.add(h.citation);
  for (const c of report.citations) {
    const m = c.claim.match(/^Controls for:\s*(.+)$/);
    if (m && !inScope.has(m[1].trim())) continue;
    if (c.source) sources.add(c.source);
  }
  return [...sources];
}
